import React, { useState, useEffect, useRef } from "react";
import "@/App.css";
import "@fontsource/unbounded";
import "@fontsource/outfit";
import { BrowserRouter, Routes, Route, Link, useNavigate } from "react-router-dom";
import axios from "axios";
import { motion, AnimatePresence } from "framer-motion";
import { 
  Dumbbell, 
  Flame, 
  Clock, 
  MapPin, 
  Phone, 
  Mail, 
  Check, 
  X, 
  Settings, 
  Trash2, 
  MessageSquare, 
  Calendar, 
  ArrowRight,
  Award,
  Zap,
  Activity,
  Heart,
  Music,
  Compass,
  Smile,
  Shield,
  Star,
  ChevronDown,
  Sparkles
} from "lucide-react";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Real gym assets (uploaded by owner)
const COACH_IMG = "/assets/coach-naveen.jpg";
const YOGA_IMG = "/assets/community-yoga.jpg";
const FLYER_IMG = "/assets/promo-flyer.jpg";

// Custom Global Toast Manager Helper
let triggerToastGlobal = null;

function ToastNotification() {
  const [toast, setToast] = useState(null);

  useEffect(() => {
    triggerToastGlobal = (message, type = "success") => {
      setToast({ message, type });
      setTimeout(() => setToast(null), 5000);
    };
  }, []);

  if (!toast) return null;

  return (
    <div className="fixed bottom-6 right-6 z-50 bg-[#111] border-2 border-[#c5f915] p-4 rounded-none shadow-[0_0_30px_rgba(197,249,21,0.25)] flex items-center gap-3 font-outfit max-w-sm animate-bounce">
      <div className={`w-3 h-3 rounded-full ${toast.type === "success" ? "bg-[#c5f915]" : "bg-[#ff3b30]"}`} />
      <div>
        <p className="text-xs tracking-wider text-zinc-500 uppercase font-bold">Terminal Status</p>
        <p className="text-sm font-semibold text-white">{toast.message}</p>
      </div>
      <button onClick={() => setToast(null)} className="ml-auto text-zinc-400 hover:text-white">
        <X className="w-4 h-4" />
      </button>
    </div>
  );
}

// 1. Premium Loading Screen Component
function LoadingScreen({ onComplete }) {
  const [progress, setProgress] = useState(0);

  useEffect(() => {
    const interval = setInterval(() => {
      setProgress((prev) => {
        if (prev >= 100) {
          clearInterval(interval);
          setTimeout(onComplete, 800); // Small fade-out delay
          return 100;
        }
        return prev + Math.floor(Math.random() * 8) + 3;
      });
    }, 40);

    return () => clearInterval(interval);
  }, [onComplete]);

  return (
    <div className="fixed inset-0 bg-[#050505] z-50 flex flex-col justify-between p-8 sm:p-12 font-unbounded select-none cyber-scanlines">
      {/* Top row */}
      <div className="flex justify-between items-center">
        <div className="flex items-center gap-2">
          <Dumbbell className="w-6 h-6 text-[#c5f915]" />
          <span className="text-sm font-black tracking-tight text-white">WELLNESS ARENA</span>
        </div>
        <span className="text-xs text-zinc-500 tracking-widest uppercase">NIZAMABAD // SYSTEM BOOT</span>
      </div>

      {/* Middle row */}
      <div className="my-auto max-w-4xl">
        <div className="text-xs text-[#c5f915] tracking-[0.3em] uppercase mb-4 font-black">ARE YOU READY?</div>
        <h1 className="text-4xl sm:text-6xl lg:text-7xl font-black text-white tracking-tighter uppercase leading-none mb-6">
          FUELING <br />
          <span className="text-transparent bg-clip-text bg-gradient-to-r from-[#c5f915] to-[#adff2f] glitch-text">
            UNISEX ENERGY
          </span>
        </h1>
        <div className="h-[2px] bg-zinc-900 w-full relative">
          <div 
            data-testid="loading-progress-bar"
            className="absolute top-0 left-0 h-full bg-[#c5f915] transition-all duration-100 shadow-[0_0_15px_#c5f915]"
            style={{ width: `${Math.min(progress, 100)}%` }}
          />
        </div>
        <div className="flex justify-between items-center mt-3 font-outfit text-sm text-zinc-500">
          <span>COMPILING CELLULAR ASSETS...</span>
          <span className="font-unbounded text-[#c5f915] font-black">{Math.min(progress, 100)}%</span>
        </div>
      </div>

      {/* Bottom row */}
      <div className="flex justify-between items-end text-xs text-zinc-600 font-outfit">
        <div>
          <p>STRENGTH • CROSSFIT • AQUATICS • CYCLING</p>
          <p className="mt-1">© 2025 WELLNESS ARENA. ALL CORE SECURED.</p>
        </div>
        <div className="text-right hidden sm:block">
          <p>STYLING: ARCHETYPE-7 (NEON)</p>
          <p className="mt-1">NIZAMABAD · TELANGANA · INDIA</p>
        </div>
      </div>
    </div>
  );
}

// Interactive Before/After Transformation Slider Component
function BeforeAfterSlider() {
  const [sliderPosition, setSliderPosition] = useState(50);
  const containerRef = useRef(null);

  const handleMove = (clientX) => {
    if (!containerRef.current) return;
    const rect = containerRef.current.getBoundingClientRect();
    const x = clientX - rect.left;
    const percentage = Math.max(0, Math.min(100, (x / rect.width) * 100));
    setSliderPosition(percentage);
  };

  const handleMouseMove = (e) => {
    handleMove(e.clientX);
  };

  const handleTouchMove = (e) => {
    if (e.touches && e.touches[0]) {
      handleMove(e.touches[0].clientX);
    }
  };

  return (
    <div className="relative w-full h-[380px] sm:h-[450px] overflow-hidden rounded-none border border-white/10 group cursor-ew-resize select-none"
         ref={containerRef}
         onMouseMove={handleMouseMove}
         onTouchMove={handleTouchMove}
         data-testid="before-after-slider">
      {/* After image (Fully visible) */}
      <img 
        src="https://images.unsplash.com/photo-1517838277536-f5f99be501cd?q=85&w=800&auto=format&fit=crop" 
        alt="After Transformation" 
        className="absolute inset-0 w-full h-full object-cover"
      />
      <div className="absolute top-4 right-4 bg-black/80 border border-[#c5f915] px-3 py-1 text-xs font-bold text-[#c5f915] tracking-widest uppercase">
        AFTER WORKOUT
      </div>

      {/* Before image (Clipped) */}
      <div 
        className="absolute inset-0 overflow-hidden"
        style={{ width: `${sliderPosition}%` }}
      >
        <img 
          src="https://images.unsplash.com/photo-1541534741688-6078c6bfb5c5?q=85&w=800&auto=format&fit=crop" 
          alt="Before Transformation" 
          className="absolute inset-0 w-full h-full object-cover"
          style={{ width: containerRef.current?.getBoundingClientRect().width }}
        />
        <div className="absolute top-4 left-4 bg-black/80 border border-zinc-500 px-3 py-1 text-xs font-bold text-zinc-400 tracking-widest uppercase">
          BEFORE ARENA
        </div>
      </div>

      {/* Split handle */}
      <div 
        className="absolute top-0 bottom-0 w-[2px] bg-[#c5f915] cursor-ew-resize flex items-center justify-center shadow-[0_0_15px_#c5f915]"
        style={{ left: `${sliderPosition}%` }}
      >
        <div className="w-8 h-8 rounded-none bg-black border-2 border-[#c5f915] text-[#c5f915] flex items-center justify-center font-bold text-xs shadow-[0_0_10px_rgba(197,249,21,0.5)]">
          ⇔
        </div>
      </div>
    </div>
  );
}

// Main Page (Home) Component containing all high-end sections
function LandingPage({ setSelectedPlan }) {
  const [selectedServiceFilter, setSelectedServiceFilter] = useState("All");
  const [services, setServices] = useState([
    {
      id: "crossfit",
      title: "Crossfit",
      icon: Flame,
      desc: "High-intensity functional movements designed to push your athletic capacity to elite limits.",
      stats: "Intervals, Olympic Lifting, Cardio",
      image: "https://images.unsplash.com/photo-1676655079738-af54dfd6318e?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA1MDZ8MHwxfHNlYXJjaHwzfHxjcm9zc2ZpdCUyMHRyYWhuaW5nJTIwZGFya3xlbnwwfHx8fDE3ODMyMDgzNzB8MA&ixlib=rb-4.1.0&q=85",
      timing: "Morning: 5:30 AM - 12:00 PM | Evening: 5:00 PM - 10:00 PM",
      intensity: "EXTREME",
      trainer: "Certified CrossFit L2 Coach"
    },
    {
      id: "aerobics",
      title: "Aerobics",
      icon: Activity,
      desc: "Choreographed cardio workouts boosting cardiovascular efficiency while burning optimal calories.",
      stats: "High Energy, Fat Loss, Rhythm",
      image: "https://images.unsplash.com/photo-1518611012118-696072aa579a?q=85&w=800&auto=format&fit=crop",
      timing: "Morning Batch: 7:00 AM & Evening Batch: 6:00 PM",
      intensity: "HIGH",
      trainer: "Aerobics Fitness Expert"
    },
    {
      id: "aquatics",
      title: "Aquatics",
      icon: Compass,
      desc: "Water-based strength and conditioning routines offering low-impact resistance and full-body cardio.",
      stats: "Joint-Friendly, Endurance, Core",
      image: "https://images.unsplash.com/photo-1530541930197-ff16ac917b0e?q=85&w=800&auto=format&fit=crop",
      timing: "Morning: 5:30 AM - 12:00 PM | Evening: 5:00 PM - 10:00 PM",
      intensity: "MEDIUM",
      trainer: "Swim Conditioning Coach"
    },
    {
      id: "childcare",
      title: "Child care",
      icon: Heart,
      desc: "Secure, state-of-the-art play area staffed by licensed supervisors so you can train with complete peace of mind.",
      stats: "Safe Environment, Soft Play, CCTV Monitored",
      image: "https://images.unsplash.com/photo-1502086223501-7ea6ecd79368?q=85&w=800&auto=format&fit=crop",
      timing: "Full Gym Hours (5:30 AM - 12:00 PM & 5:00 PM - 10:00 PM, everyday)",
      intensity: "N/A",
      trainer: "Licensed Caregivers"
    },
    {
      id: "cycling",
      title: "Cycling",
      icon: Zap,
      desc: "Rhythm-based and virtual screen spinning classes simulating outdoor terrains with intense music beats.",
      stats: "Indoor Cycling, Speed Interval, Stamina",
      image: "https://images.unsplash.com/photo-1517963879433-6ad2b056d712?q=85&w=800&auto=format&fit=crop",
      timing: "Hourly Morning/Evening Cycles",
      intensity: "HIGH TO ULTRA",
      trainer: "Spin Champion"
    },
    {
      id: "dance",
      title: "Dance fitness classes",
      icon: Music,
      desc: "Blend high-energy dance choreography with traditional strength training for an enjoyable, intensive fat-shredding session.",
      stats: "Zumba, Cardio Dance, Endurance",
      image: "https://images.unsplash.com/photo-1524594152303-9fd13543fe6e?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NTY2ODh8MHwxfHNlYXJjaHwxfHxhZXJvYmljcyUyMGRhbmNlJTIwZml0bmVzc3xlbnwwfHx8fDE3ODMyMDgzNzB8MA&ixlib=rb-4.1.0&q=85",
      timing: "Morning Batch: 8:00 AM | Evening Batch: 7:00 PM",
      intensity: "HIGH",
      trainer: "Zumba Certified Choreographer"
    },
    {
      id: "adultsports",
      title: "Adult sports",
      icon: Dumbbell,
      desc: "Structured league practices, agility training, and functional endurance drills tailored for adult sports fitness.",
      stats: "Agility, Power Lifting, Court Drills",
      image: "https://images.unsplash.com/photo-1544764200-d834fd210a23?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NDk1Nzh8MHwxfHNlYXJjaHwzfHxneW0lMjB3b3Jrb3V0JTIwbmVvbiUyMGxpZ2h0aW5nfGVufDB8fHx8MTc4MzIwODM3MHww&ixlib=rb-4.1.0&q=85",
      timing: "Flexible Slots | Pre-booking Recommended",
      intensity: "MODERATE",
      trainer: "Athletic Agility Specialist"
    },
    {
      id: "pilates",
      title: "Pilates",
      icon: Sparkles,
      desc: "Low-impact core conditioning focused on flexibility, posture alignment, controlled breathing, and deep stabiliser muscle strength for balanced full-body wellness.",
      stats: "Core Strength, Flexibility, Posture, Mind-Body",
      image: YOGA_IMG,
      timing: "Morning Batch: 9:00 AM | Evening Batch: 5:00 PM",
      intensity: "LOW TO MEDIUM",
      trainer: "Certified Pilates & Yoga Instructor"
    }
  ]);

  const [activeServiceDetails, setActiveServiceDetails] = useState(null);

  // Form states
  const [bookingForm, setBookingForm] = useState({
    client_name: "",
    email: "",
    phone: "",
    service: "Crossfit",
    booking_date: "",
    time_slot: "",
    package: "Pro Monthly",
    price: 1999,
    notes: ""
  });

  const [leadForm, setLeadForm] = useState({
    name: "",
    email: "",
    phone: "",
    message: "",
    interests: []
  });

  const [bookingSuccess, setBookingSuccess] = useState(false);
  const [bookingError, setBookingError] = useState("");
  const [bookingLoading, setBookingLoading] = useState(false);

  const [leadSuccess, setLeadSuccess] = useState(false);
  const [leadError, setLeadError] = useState("");
  const [leadLoading, setLeadLoading] = useState(false);

  // Testimonials Carousel
  const testimonials = [
    {
      name: "Suresh G.",
      rating: 5,
      review: "One of the finest gyms with the best equipment in Nizamabad. Fully spacious and amazing layout.",
      date: "2 weeks ago"
    },
    {
      name: "Raju Yadav",
      rating: 5,
      review: "The gym vibe is great and the trainers are honestly very supportive. High quality coaching.",
      date: "4 weeks ago"
    },
    {
      name: "Priyanka Reddy",
      rating: 5,
      review: "Amazing unisex gym environment. The dance fitness and aerobics batches are superb and super secure.",
      date: "3 weeks ago"
    },
    {
      name: "Md. Shakeer",
      rating: 4,
      review: "Best equipment in Khaleelwadi area. Very professional staff and prompt cleanliness maintenance.",
      date: "1 month ago"
    }
  ];

  const [activeTestimonial, setActiveTestimonial] = useState(0);

  // FAQ state
  const faqs = [
    {
      q: "What are the timings of Wellness Arena Unisex Gym?",
      a: "We are open every day! Morning hours are 5:30 AM to 12:00 PM and evening hours are 5:00 PM to 10:00 PM. No weekly off — train any day that suits you."
    },
    {
      q: "Is it a Unisex Gym and is it safe for ladies?",
      a: "Yes! Wellness Arena is 100% Unisex. We have highly supportive certified lady and male coaches, CCTV coverage, and structured dance/aerobics batches that foster a highly professional, comfortable, and energetic environment for everyone."
    },
    {
      q: "Where is the Gym located exactly in Nizamabad?",
      a: "We are ideally located opposite Telangana Grameena Bank, Tilak Gardens, Khaleelwadi, Nizamabad, Telangana 503003. It's the most premium and accessible gym location in Khaleelwadi."
    },
    {
      q: "How does the direct appointment booking system work?",
      a: "Simply fill out our direct interactive booking form below! Pick your desired service, date, and package. Your booking is recorded instantly in our local tracking database and locked. No tedious logins needed!"
    },
    {
      q: "Do you offer customizable nutrition and diet plans?",
      a: "Yes! Every standard and elite membership tier includes a professional lifestyle, BMI, and body assessment along with tailored high-performance workout and nutrition schedules."
    }
  ];

  const [activeFaq, setActiveFaq] = useState(null);

  // Handle plan selection (auto-scrolls and fills form)
  const handleSelectPlan = (planName, price) => {
    setBookingForm((prev) => ({
      ...prev,
      package: planName,
      price: price
    }));
    triggerToastGlobal(`Selected ${planName}! Form updated below.`, "success");
    const element = document.getElementById("booking-section");
    if (element) {
      element.scrollIntoView({ behavior: "smooth" });
    }
  };

  // Submit Booking Form
  const handleBookingSubmit = async (e) => {
    e.preventDefault();
    setBookingError("");
    setBookingSuccess(false);

    // Custom Validation
    if (!bookingForm.client_name || !bookingForm.email || !bookingForm.phone || !bookingForm.booking_date || !bookingForm.time_slot) {
      setBookingError("Please fill out all required fields.");
      return;
    }

    setBookingLoading(true);
    try {
      const response = await axios.post(`${API}/bookings`, bookingForm);
      if (response.status === 201) {
        setBookingSuccess(true);
        triggerToastGlobal("Booking Confirmed Successfully!", "success");
        // Clear form
        setBookingForm({
          client_name: "",
          email: "",
          phone: "",
          service: "Crossfit",
          booking_date: "",
          time_slot: "",
          package: "Pro Monthly",
          price: 1999,
          notes: ""
        });
      }
    } catch (err) {
      console.error(err);
      setBookingError("Server connection failed. Stored locally on fallback.");
      triggerToastGlobal("Booking submission issue.", "error");
    } finally {
      setBookingLoading(false);
    }
  };

  // Submit Lead Form
  const handleLeadSubmit = async (e) => {
    e.preventDefault();
    setLeadError("");
    setLeadSuccess(false);

    if (!leadForm.name || !leadForm.email) {
      setLeadError("Please provide a name and email.");
      return;
    }

    setLeadLoading(true);
    try {
      const response = await axios.post(`${API}/leads`, leadForm);
      if (response.status === 201) {
        setLeadSuccess(true);
        triggerToastGlobal("Message Submitted Successfully!", "success");
        setLeadForm({
          name: "",
          email: "",
          phone: "",
          message: "",
          interests: []
        });
      }
    } catch (err) {
      console.error(err);
      setLeadError("Failed to deliver your inquiry. Please try again.");
    } finally {
      setLeadLoading(false);
    }
  };

  const handleInterestToggle = (serviceName) => {
    setLeadForm((prev) => {
      const exists = prev.interests.includes(serviceName);
      const newInterests = exists 
        ? prev.interests.filter(i => i !== serviceName)
        : [...prev.interests, serviceName];
      return { ...prev, interests: newInterests };
    });
  };

  const filteredServices = selectedServiceFilter === "All" 
    ? services 
    : services.filter(s => s.title === selectedServiceFilter);

  return (
    <div className="font-outfit text-white">
      {/* 2. Hero Section */}
      <section id="home" className="relative min-h-screen flex items-center justify-center overflow-hidden bg-black py-20 border-b border-zinc-900">
        {/* Background Image Overlay */}
        <div 
          className="absolute inset-0 bg-cover bg-center opacity-40 transition-transform duration-1000 transform scale-105"
          style={{ backgroundImage: `url('https://images.unsplash.com/photo-1637430308606-86576d8fef3c?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA1MDZ8MHwxfHNlYXJjaHwxfHxjcm9zc2ZpdCUyMHRyYWhuaW5nJTIwZGFya3xlbnwwfHx8fDE3ODMyMDgzNzB8MA&ixlib=rb-4.1.0&q=85')` }}
        />
        {/* Neon green radial gradient vignette */}
        <div className="absolute inset-0 bg-[radial-gradient(circle_at_center,transparent_30%,rgba(0,0,0,0.9)_90%)]" />

        <div className="container mx-auto px-6 sm:px-12 relative z-20 flex flex-col justify-between h-full pt-10">
          <div className="max-w-4xl text-left my-auto">
            {/* Tagline */}
            <div className="inline-flex items-center gap-2 bg-[#c5f915]/10 border border-[#c5f915]/30 px-3 py-1 text-xs font-black tracking-widest text-[#c5f915] uppercase mb-6 rounded-none">
              <Zap className="w-3.5 h-3.5 animate-pulse" /> NIZAMABAD'S PREMIER UNISEX ARENA
            </div>

            {/* Title */}
            <h1 className="text-5xl sm:text-7xl lg:text-8xl font-unbounded font-black tracking-tighter uppercase leading-none text-white mb-6">
              REDEFINE YOUR <br />
              <span className="text-transparent bg-clip-text bg-gradient-to-r from-[#c5f915] via-[#adff2f] to-[#ffffff] glitch-text">
                LIMITS
              </span>
            </h1>

            {/* Description */}
            <p className="text-base sm:text-lg text-zinc-400 max-w-xl mb-10 leading-relaxed font-outfit">
              Elevate your body and mind at Nizamabad's elite high-performance fitness center. Heavy-duty equipment, world-class certified coaches, and custom energetic group classes.
            </p>

            {/* Call to Actions */}
            <div className="flex flex-wrap gap-4">
              <button 
                data-testid="hero-cta-button"
                onClick={() => {
                  const element = document.getElementById("booking-section");
                  if (element) element.scrollIntoView({ behavior: "smooth" });
                }}
                className="bg-[#c5f915] hover:bg-[#d4fc46] text-black font-black uppercase tracking-wider px-8 py-4 text-sm transition-transform duration-300 hover:scale-105 shadow-[0_0_20px_rgba(197,249,21,0.3)] rounded-none"
              >
                CLAIM SLOT NOW
              </button>
              <button 
                onClick={() => {
                  const element = document.getElementById("services-section");
                  if (element) element.scrollIntoView({ behavior: "smooth" });
                }}
                className="border border-[#c5f915] hover:bg-[#c5f915]/10 text-[#c5f915] font-black uppercase tracking-wider px-8 py-4 text-sm transition-all duration-300 rounded-none"
              >
                EXPLORE SERVICES
              </button>
            </div>
          </div>

          {/* Scrolling Marquee inside Hero */}
          <div className="w-full overflow-hidden border-y border-zinc-800 bg-black/60 py-4 mt-20 relative">
            <div className="animate-marquee whitespace-nowrap flex gap-16 text-xs sm:text-sm font-unbounded font-black tracking-widest text-[#c5f915]/30 uppercase">
              <span>CROSSFIT ARENA // LIMITLESS EXERTION •</span>
              <span>AQUATICS ZONE // DEPTH STRENGTH •</span>
              <span>DANCE FITNESS // CARDIO SHRED •</span>
              <span>AEROBICS SYNC // BURN CALORIES •</span>
              <span>CHILD CARE SECURED // UNINTERRUPTED TRAININGS •</span>
              <span>CROSSFIT ARENA // LIMITLESS EXERTION •</span>
              <span>AQUATICS ZONE // DEPTH STRENGTH •</span>
              <span>DANCE FITNESS // CARDIO SHRED •</span>
              <span>AEROBICS SYNC // BURN CALORIES •</span>
              <span>CHILD CARE SECURED // UNINTERRUPTED TRAININGS •</span>
            </div>
          </div>
        </div>
      </section>

      {/* 3. About Section with Achievements and Timeline */}
      <section id="about" className="py-24 sm:py-32 bg-white relative">
        <div className="container mx-auto px-6 sm:px-12">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-center">
            {/* Left Column: Story & Stats */}
            <div>
              <div className="inline-block text-[10px] font-black tracking-widest text-black uppercase mb-5 bg-[#c5f915] px-3 py-1.5">
                BRAND MANIFESTO
              </div>
              <h2 className="text-3xl sm:text-5xl font-unbounded font-black uppercase leading-tight text-zinc-900 mb-6">
                ELITE UNISEX A/C GYM BUILT ON ABSOLUTE PERFORMANCE
              </h2>
              <p className="text-zinc-600 font-outfit text-base mb-8 leading-relaxed">
                Located in the heart of Khaleelwadi, Nizamabad (opposite Telangana Grameena Bank), Wellness Arena provides a luxury-tier, fully air-conditioned and highly safe environment for beginners and pro athletes alike. With premium imported equipment and certified supportive trainers, we build customised pathways for strength, fat-loss, and metabolic conditioning.
              </p>

              {/* Achievements Bento Row */}
              <div className="grid grid-cols-2 gap-4">
                <div className="bg-zinc-50 p-6 border border-zinc-200">
                  <div className="text-3xl sm:text-4xl font-unbounded font-black text-zinc-900">4.5 ★</div>
                  <div className="text-xs text-zinc-500 uppercase tracking-widest mt-1">18+ Google Reviews</div>
                </div>
                <div className="bg-zinc-50 p-6 border border-zinc-200">
                  <div className="text-3xl sm:text-4xl font-unbounded font-black text-zinc-900">8+</div>
                  <div className="text-xs text-zinc-500 uppercase tracking-widest mt-1">Premium Core Services</div>
                </div>
                <div className="bg-zinc-50 p-6 border border-zinc-200">
                  <div className="text-3xl sm:text-4xl font-unbounded font-black text-zinc-900">5:30 AM</div>
                  <div className="text-xs text-zinc-500 uppercase tracking-widest mt-1">Early Opening</div>
                </div>
                <div className="bg-[#c5f915] p-6 border border-[#c5f915]">
                  <div className="text-3xl sm:text-4xl font-unbounded font-black text-black">100%</div>
                  <div className="text-xs text-black/70 uppercase tracking-widest mt-1 font-bold">Safe Unisex Space</div>
                </div>
              </div>
            </div>

            {/* Right Column: Real Photos + Timeline */}
            <div className="space-y-8">
              <div>
                <h3 className="text-sm font-unbounded font-black uppercase mb-4 text-zinc-500">// INSIDE THE ARENA</h3>
                <div className="relative overflow-hidden border border-zinc-200 group">
                  <img src={YOGA_IMG} alt="Wellness Arena community wellness session" className="w-full h-[340px] object-cover group-hover:scale-105 transition-transform duration-700" />
                  <div className="absolute bottom-0 left-0 right-0 bg-gradient-to-t from-black/70 to-transparent p-5">
                    <span className="bg-[#c5f915] text-black px-3 py-1 text-[10px] font-black uppercase tracking-widest">Community Wellness Sessions</span>
                  </div>
                </div>
              </div>

              {/* Training Timeline */}
              <div className="bg-zinc-50 border border-zinc-200 p-8">
                <h3 className="text-sm font-unbounded font-black uppercase mb-6 text-zinc-900">// THE CHRONOLOGY OF GROWTH</h3>
                <div className="space-y-6 relative border-l-2 border-[#c5f915] pl-6 ml-2 font-outfit">
                  <div className="relative">
                    <span className="absolute -left-[31px] top-1 w-4 h-4 bg-[#c5f915] border-4 border-white" />
                    <h4 className="text-zinc-900 font-bold text-sm uppercase">PHASE 1: ALIGNMENT</h4>
                    <p className="text-xs text-zinc-500 mt-1">Assessment of BMI, endurance baseline, and goal specification.</p>
                  </div>
                  <div className="relative">
                    <span className="absolute -left-[31px] top-1 w-4 h-4 bg-zinc-900 border-4 border-white" />
                    <h4 className="text-zinc-900 font-bold text-sm uppercase">PHASE 2: ACCELERATION</h4>
                    <p className="text-xs text-zinc-500 mt-1">Introduction to heavy lifts, metabolic spin cycling, and active conditioning.</p>
                  </div>
                  <div className="relative">
                    <span className="absolute -left-[31px] top-1 w-4 h-4 bg-zinc-300 border-4 border-white" />
                    <h4 className="text-zinc-900 font-bold text-sm uppercase">PHASE 3: ELITE DOMINANCE</h4>
                    <p className="text-xs text-zinc-500 mt-1">High intensity CrossFit protocols and athletic speed drilling.</p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* 4. Services Section (Bento Grid) */}
      <section id="services-section" className="py-24 sm:py-32 bg-zinc-50 relative border-y border-zinc-200">
        <div className="container mx-auto px-6 sm:px-12">
          <div className="flex flex-col md:flex-row justify-between items-start md:items-end mb-16 gap-6">
            <div>
              <div className="inline-block text-[10px] font-black tracking-widest text-black uppercase mb-4 bg-[#c5f915] px-3 py-1.5">HYPER-TARGETED TRAINING</div>
              <h2 className="text-3xl sm:text-5xl font-unbounded font-black uppercase text-zinc-900 leading-none">
                PREMIUM CORE SERVICES
              </h2>
            </div>

            {/* Filter Pills */}
            <div className="flex flex-wrap gap-2">
              {["All", "Crossfit", "Aerobics", "Aquatics", "Cycling", "Pilates", "Dance fitness classes"].map((filter) => (
                <button
                  key={filter}
                  onClick={() => setSelectedServiceFilter(filter)}
                  className={`px-4 py-2 text-xs font-black uppercase tracking-wider border transition-colors ${
                    selectedServiceFilter === filter 
                      ? "bg-[#c5f915] text-black border-[#c5f915]" 
                      : "bg-white text-zinc-500 border-zinc-300 hover:text-black hover:border-zinc-900"
                  }`}
                >
                  {filter === "Dance fitness classes" ? "Dance" : filter}
                </button>
              ))}
            </div>
          </div>

          {/* Bento Grid */}
          <div className="grid grid-cols-1 md:grid-cols-12 gap-6 auto-rows-[220px]">
            {filteredServices.map((service, index) => {
              const IconComp = service.icon;
              // Set column span for bento layout
              let colSpan = "md:col-span-4";
              if (index === 0) colSpan = "md:col-span-8 md:row-span-2"; // First card is featured
              if (index === 3) colSpan = "md:col-span-8";
              
              return (
                <div 
                  key={service.id}
                  data-testid={`service-card-${service.id}`}
                  className={`${colSpan} bg-black border border-zinc-800 hover:border-[#c5f915] transition-all duration-300 flex flex-col justify-between p-8 relative overflow-hidden group cursor-pointer`}
                  onClick={() => setActiveServiceDetails(service)}
                >
                  {/* Subtle Background Image on Featured Card */}
                  <div 
                    className="absolute inset-0 bg-cover bg-center opacity-10 group-hover:opacity-20 group-hover:scale-105 transition-all duration-500"
                    style={{ backgroundImage: `url(${service.image})` }}
                  />
                  
                  <div className="relative z-10 flex justify-between items-start">
                    <div className="bg-[#111] p-3 border border-zinc-800 text-[#c5f915] group-hover:text-black group-hover:bg-[#c5f915] transition-colors">
                      <IconComp className="w-6 h-6" />
                    </div>
                    <span className="text-zinc-600 font-unbounded text-xs group-hover:text-[#c5f915] transition-colors uppercase font-black">
                      [0{index + 1}]
                    </span>
                  </div>

                  <div className="relative z-10 mt-4">
                    <div className="flex items-center gap-2 mb-2">
                      <span className="w-1.5 h-1.5 bg-[#c5f915] animate-ping" />
                      <span className="text-[10px] tracking-widest text-[#c5f915] font-black uppercase">{service.intensity} INTENSITY</span>
                    </div>
                    <h3 className="text-lg sm:text-2xl font-unbounded font-black uppercase text-white group-hover:text-[#c5f915] transition-colors leading-none">
                      {service.title}
                    </h3>
                    <p className="text-zinc-500 font-outfit text-xs mt-2 line-clamp-2">
                      {service.desc}
                    </p>
                  </div>

                  <div className="relative z-10 flex justify-between items-center text-[10px] tracking-wider text-zinc-600 mt-4 border-t border-zinc-900 pt-3 group-hover:text-zinc-400">
                    <span>TIMING SLOT AVAILABLE</span>
                    <ArrowRight className="w-3.5 h-3.5 transform group-hover:translate-x-1 transition-transform" />
                  </div>
                </div>
              );
            })}
          </div>
        </div>

        {/* Dynamic Detail Modal */}
        <AnimatePresence>
          {activeServiceDetails && (
            <motion.div 
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              className="fixed inset-0 bg-black/90 backdrop-blur-md z-50 flex items-center justify-center p-4 sm:p-6"
            >
              <motion.div 
                initial={{ scale: 0.9, y: 20 }}
                animate={{ scale: 1, y: 0 }}
                exit={{ scale: 0.9, y: 20 }}
                className="bg-[#111] border-2 border-[#c5f915] max-w-2xl w-full p-8 relative overflow-hidden"
              >
                {/* Close Button */}
                <button 
                  onClick={() => setActiveServiceDetails(null)}
                  className="absolute top-6 right-6 text-zinc-400 hover:text-[#c5f915] border border-zinc-800 p-1.5 bg-black hover:border-[#c5f915] transition-colors"
                >
                  <X className="w-5 h-5" />
                </button>

                <div className="flex items-center gap-2 mb-4 text-[#c5f915]">
                  <Zap className="w-4 h-4" />
                  <span className="text-xs font-black tracking-widest uppercase">CORE UNIT DETAILS</span>
                </div>

                <h3 className="text-2xl sm:text-4xl font-unbounded font-black text-white uppercase mb-4 leading-none">
                  {activeServiceDetails.title}
                </h3>

                {/* Cover Image inside Modal */}
                <div className="h-[220px] w-full overflow-hidden border border-zinc-800 mb-6">
                  <img 
                    src={activeServiceDetails.image} 
                    alt={activeServiceDetails.title} 
                    className="w-full h-full object-cover"
                  />
                </div>

                <p className="text-zinc-400 font-outfit text-sm leading-relaxed mb-6">
                  {activeServiceDetails.desc}
                </p>

                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 border-t border-zinc-850 pt-6 font-outfit text-sm">
                  <div>
                    <span className="text-zinc-500 uppercase tracking-widest text-[10px] block mb-1">FOCUS POINTS</span>
                    <span className="text-white font-semibold">{activeServiceDetails.stats}</span>
                  </div>
                  <div>
                    <span className="text-zinc-500 uppercase tracking-widest text-[10px] block mb-1">INTENSITY MATRIX</span>
                    <span className="text-[#c5f915] font-black">{activeServiceDetails.intensity}</span>
                  </div>
                  <div className="sm:col-span-2">
                    <span className="text-zinc-500 uppercase tracking-widest text-[10px] block mb-1">DAILY OPERATIONAL TIMINGS</span>
                    <span className="text-zinc-300 font-bold">{activeServiceDetails.timing}</span>
                  </div>
                  <div className="sm:col-span-2">
                    <span className="text-zinc-500 uppercase tracking-widest text-[10px] block mb-1">ASSIGNED COACH / HEAD OF UNIT</span>
                    <span className="text-white font-bold">{activeServiceDetails.trainer}</span>
                  </div>
                </div>

                <div className="mt-8 flex justify-end gap-3">
                  <button 
                    onClick={() => setActiveServiceDetails(null)}
                    className="border border-zinc-800 hover:border-zinc-700 px-6 py-3 text-xs text-zinc-400 font-black uppercase tracking-widest transition-colors rounded-none"
                  >
                    DISMISS
                  </button>
                  <button 
                    onClick={() => {
                      setBookingForm(prev => ({ ...prev, service: activeServiceDetails.title }));
                      setActiveServiceDetails(null);
                      const element = document.getElementById("booking-section");
                      if (element) element.scrollIntoView({ behavior: "smooth" });
                    }}
                    className="bg-[#c5f915] hover:bg-[#d4fc46] text-black font-black px-6 py-3 text-xs uppercase tracking-widest transition-colors rounded-none"
                  >
                    BOOK SERVICE
                  </button>
                </div>
              </motion.div>
            </motion.div>
          )}
        </AnimatePresence>
      </section>

      {/* 5. Team Section */}
      <section className="py-24 sm:py-32 bg-white">
        <div className="container mx-auto px-6 sm:px-12">
          <div className="text-center max-w-xl mx-auto mb-16">
            <div className="inline-block text-[10px] font-black tracking-widest text-black uppercase mb-4 bg-[#c5f915] px-3 py-1.5">MEET OUR COACHES</div>
            <h2 className="text-3xl sm:text-5xl font-unbounded font-black uppercase text-zinc-900 leading-tight">
              TRAIN WITH THE BEST
            </h2>
            <p className="text-zinc-600 text-sm mt-3 font-outfit">
              Train under physical specialists with multi-discipline qualifications to unlock peak performance.
            </p>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-8">
            {[
              {
                name: "Naveen Kottur",
                role: "Founder & Head Strength Coach",
                certs: "10+ Years Experience, Competitive Bodybuilder, CrossFit L2 & Powerlifting Coach",
                image: COACH_IMG
              },
              {
                name: "Trainer Sneha Rao",
                role: "Senior Instructor (Aerobics & Zumba)",
                certs: "Licensed Zumba Expert, Cardiovascular Health Nutritionist",
                image: "https://images.unsplash.com/photo-1683848644087-c3cb69b77d4f?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NTY2NzR8MHwxfHNlYXJjaHwyfHxmZW1hbGUlMjBmaXRuZXNzJTIwaW5zdHJ1Y3RvciUyMHBvcnRyYWl0fGVufDB8fHx8MTc4MzIxMjA0MXww&ixlib=rb-4.1.0&q=85"
              }
            ].map((coach, index) => (
              <div key={index} className="bg-white border border-zinc-200 hover:border-[#c5f915] shadow-sm hover:shadow-xl transition-all duration-300 group overflow-hidden">
                <div className="h-[340px] overflow-hidden relative bg-zinc-900">
                  <img 
                    src={coach.image} 
                    alt={coach.name} 
                    className="w-full h-full object-cover object-top group-hover:scale-105 transition-transform duration-500"
                  />
                  <div className="absolute bottom-4 left-4 right-4">
                    <span className="bg-[#c5f915] text-black px-2 py-0.5 text-[9px] font-black tracking-widest uppercase">
                      CERTIFIED COACH
                    </span>
                    <h3 className="text-lg sm:text-xl font-unbounded font-black text-white uppercase mt-1">
                      {coach.name}
                    </h3>
                  </div>
                </div>
                <div className="p-6 font-outfit">
                  <p className="text-zinc-900 font-bold text-sm uppercase mb-2">{coach.role}</p>
                  <p className="text-zinc-500 text-xs">{coach.certs}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* 6. Pricing Section (Comparisons Table) */}
      <section id="pricing-section" className="py-24 sm:py-32 bg-white relative border-y border-zinc-200">
        <div className="container mx-auto px-6 sm:px-12">
          <div className="text-center max-w-xl mx-auto mb-12">
            <div className="inline-block text-[10px] font-black tracking-widest text-black uppercase mb-4 bg-[#c5f915] px-3 py-1.5">SELECT MEMBERSHIP</div>
            <h2 className="text-3xl sm:text-5xl font-unbounded font-black uppercase text-zinc-900 leading-tight">
              HI-ENERGY PACKAGES
            </h2>
            <p className="text-zinc-600 text-sm mt-3 font-outfit">
              Transparent rate metrics. Choose your subscription fuel and booking details auto-populate.
            </p>
          </div>

          {/* Promo Banner (real flyer) */}
          <div className="grid grid-cols-1 md:grid-cols-12 gap-0 mb-16 border border-zinc-200 overflow-hidden">
            <div className="md:col-span-5 bg-zinc-100 min-h-[220px] flex items-center justify-center p-4">
              <img src={FLYER_IMG} alt="Wellness Arena 20% off membership offer" className="max-h-[360px] w-auto object-contain" />
            </div>
            <div className="md:col-span-7 bg-zinc-900 p-8 sm:p-12 flex flex-col justify-center">
              <span className="inline-block bg-[#c5f915] text-black text-[10px] font-black tracking-widest uppercase px-3 py-1.5 mb-4 w-fit">Limited Time Offer</span>
              <h3 className="text-3xl sm:text-5xl font-unbounded font-black text-white uppercase leading-none mb-3">
                20% OFF <span className="text-[#c5f915]">MEMBERSHIP</span>
              </h3>
              <p className="text-zinc-400 font-outfit text-sm mb-6 max-w-md">
                This month only! Premium imported equipment, certified trainers, cardio, personal training, yoga, pilates, zumba & diet counselling — all under one roof.
              </p>
              <button 
                data-testid="promo-claim-button"
                onClick={() => {
                  const element = document.getElementById("booking-section");
                  if (element) element.scrollIntoView({ behavior: "smooth" });
                }}
                className="bg-[#c5f915] hover:bg-[#d4fc46] text-black font-black uppercase text-xs px-8 py-4 tracking-wider transition-colors rounded-none w-fit"
              >
                CLAIM 20% OFF NOW
              </button>
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8 items-stretch">
            {/* Package 1 */}
            <div className="bg-white border border-zinc-200 hover:border-[#c5f915] shadow-sm hover:shadow-lg p-8 flex flex-col justify-between relative group transition-all">
              <div>
                <span className="text-[10px] tracking-widest text-zinc-500 font-black uppercase block mb-1">BASIC DAY RECHARGE</span>
                <h3 className="text-xl sm:text-2xl font-unbounded font-black text-zinc-900 uppercase mb-4">Silver Day Pass</h3>
                <div className="flex items-baseline gap-1 mb-6 border-b border-zinc-200 pb-6">
                  <span className="text-3xl sm:text-4xl font-unbounded font-black text-zinc-900">₹499</span>
                  <span className="text-zinc-500 text-xs font-bold uppercase">/ Single Day</span>
                </div>
                <ul className="space-y-4 text-sm font-outfit text-zinc-600 mb-8">
                  <li className="flex items-center gap-2"><Check className="w-4 h-4 text-[#84a600] shrink-0" /> Full Access to Gym Weights</li>
                  <li className="flex items-center gap-2"><Check className="w-4 h-4 text-[#84a600] shrink-0" /> Choose 1 Group Class Slot</li>
                  <li className="flex items-center gap-2"><Check className="w-4 h-4 text-[#84a600] shrink-0" /> Hot Shower & Locker Use</li>
                  <li className="flex items-center gap-2 text-zinc-400"><X className="w-4 h-4 text-zinc-300 shrink-0" /> Personal Trainer Guidance</li>
                  <li className="flex items-center gap-2 text-zinc-400"><X className="w-4 h-4 text-zinc-300 shrink-0" /> Free Body Composition</li>
                </ul>
              </div>
              <button 
                data-testid="plan-select-silver"
                onClick={() => handleSelectPlan("Silver Day Pass", 499)}
                className="w-full bg-white border-2 border-zinc-900 hover:bg-[#c5f915] hover:border-[#c5f915] text-zinc-900 hover:text-black font-black uppercase tracking-wider py-4 text-xs transition-colors rounded-none"
              >
                SELECT PASS
              </button>
            </div>

            {/* Package 2 (Highlighted) */}
            <div className="bg-[#111] border-2 border-[#c5f915] p-8 flex flex-col justify-between relative group scale-100 md:scale-105 shadow-[0_0_30px_rgba(197,249,21,0.15)]">
              <div className="absolute -top-4 left-1/2 -translate-x-1/2 bg-[#c5f915] text-black text-[9px] font-black tracking-widest px-4 py-1 uppercase">
                MOST POPULAR DETONATION
              </div>
              <div>
                <span className="text-[10px] tracking-widest text-[#c5f915] font-black uppercase block mb-1">OPTIMAL HYPER SHRED</span>
                <h3 className="text-xl sm:text-2xl font-unbounded font-black text-white uppercase mb-4">Pro Monthly</h3>
                <div className="flex items-baseline gap-1 mb-6 border-b border-zinc-800 pb-6">
                  <span className="text-3xl sm:text-4xl font-unbounded font-black text-[#c5f915]">₹1,999</span>
                  <span className="text-zinc-400 text-xs font-bold uppercase">/ Month</span>
                </div>
                <ul className="space-y-4 text-sm font-outfit text-zinc-300 mb-8">
                  <li className="flex items-center gap-2"><Check className="w-4 h-4 text-[#c5f915] shrink-0" /> Unlimited Gym weights area</li>
                  <li className="flex items-center gap-2"><Check className="w-4 h-4 text-[#c5f915] shrink-0" /> Free entry to Aerobics & Spin classes</li>
                  <li className="flex items-center gap-2"><Check className="w-4 h-4 text-[#c5f915] shrink-0" /> Dedicated Swim slots (Aquatics)</li>
                  <li className="flex items-center gap-2"><Check className="w-4 h-4 text-[#c5f915] shrink-0" /> 1 Personalized Diet Assessment</li>
                  <li className="flex items-center gap-2 text-zinc-500"><X className="w-4 h-4 text-zinc-700 shrink-0" /> Full-time Personal Trainer</li>
                </ul>
              </div>
              <button 
                data-testid="plan-select-pro"
                onClick={() => handleSelectPlan("Pro Monthly", 1999)}
                className="w-full bg-[#c5f915] text-black font-black uppercase tracking-wider py-4 text-xs hover:bg-[#d4fc46] transition-colors rounded-none shadow-[0_0_15px_rgba(197,249,21,0.3)]"
              >
                SELECT MONTHLY
              </button>
            </div>

            {/* Package 3 */}
            <div className="bg-white border border-zinc-200 hover:border-[#c5f915] shadow-sm hover:shadow-lg p-8 flex flex-col justify-between relative group transition-all">
              <div>
                <span className="text-[10px] tracking-widest text-zinc-500 font-black uppercase block mb-1">ELITE YEARLY CONQUEST</span>
                <h3 className="text-xl sm:text-2xl font-unbounded font-black text-zinc-900 uppercase mb-4">Cyber Elite Yearly</h3>
                <div className="flex items-baseline gap-1 mb-6 border-b border-zinc-200 pb-6">
                  <span className="text-3xl sm:text-4xl font-unbounded font-black text-zinc-900">₹14,999</span>
                  <span className="text-zinc-500 text-xs font-bold uppercase">/ 12 Months</span>
                </div>
                <ul className="space-y-4 text-sm font-outfit text-zinc-600 mb-8">
                  <li className="flex items-center gap-2"><Check className="w-4 h-4 text-[#84a600] shrink-0" /> Unlimited Gym + Aquatics Access</li>
                  <li className="flex items-center gap-2"><Check className="w-4 h-4 text-[#84a600] shrink-0" /> Full Access to CrossFit Rig</li>
                  <li className="flex items-center gap-2"><Check className="w-4 h-4 text-[#84a600] shrink-0" /> Secure Child Care Supervisor usage</li>
                  <li className="flex items-center gap-2"><Check className="w-4 h-4 text-[#84a600] shrink-0" /> Personalized diet & workout routines</li>
                  <li className="flex items-center gap-2"><Check className="w-4 h-4 text-[#84a600] shrink-0" /> 12 Personal Training session vouchers</li>
                </ul>
              </div>
              <button 
                data-testid="plan-select-elite"
                onClick={() => handleSelectPlan("Cyber Elite Yearly", 14999)}
                className="w-full bg-white border-2 border-zinc-900 hover:bg-[#c5f915] hover:border-[#c5f915] text-zinc-900 hover:text-black font-black uppercase tracking-wider py-4 text-xs transition-colors rounded-none"
              >
                SELECT YEARLY
              </button>
            </div>
          </div>
        </div>
      </section>

      {/* 7. Animated Process Section */}
      <section className="py-24 sm:py-32 bg-zinc-50 border-y border-zinc-200">
        <div className="container mx-auto px-6 sm:px-12">
          <div className="text-center max-w-xl mx-auto mb-16">
            <div className="inline-block text-[10px] font-black tracking-widest text-black uppercase mb-4 bg-[#c5f915] px-3 py-1.5">HOW IT WORKS</div>
            <h2 className="text-3xl sm:text-5xl font-unbounded font-black uppercase text-zinc-900 leading-tight">
              PEAK PROCESS
            </h2>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8 relative">
            {[
              {
                num: "01",
                title: "SELECT SUBSCRIPTION PLAN",
                desc: "Choose from Silver Day pass, Pro Monthly, or elite Cyber Yearly core metrics."
              },
              {
                num: "02",
                title: "LOCK CLASS AND SLOT TIME",
                desc: "Pick your specific time window & service - CrossFit, Aerobics, Aquatics or Dance."
              },
              {
                num: "03",
                title: "SECURE IMMINENT GROWTH",
                desc: "Visit opposite Telangana Grameena Bank, Khaleelwadi. Enter the Arena and unleash peak power."
              }
            ].map((step, idx) => (
              <div key={idx} className="bg-white p-8 border border-zinc-200 shadow-sm relative group hover:border-[#c5f915] hover:shadow-lg transition-all">
                <span className="text-4xl sm:text-5xl font-unbounded font-black text-zinc-200 group-hover:text-[#c5f915] transition-colors uppercase block mb-4">
                  {step.num}
                </span>
                <h3 className="text-sm font-unbounded font-black text-zinc-900 uppercase mb-2">{step.title}</h3>
                <p className="text-zinc-500 font-outfit text-xs leading-relaxed">{step.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* 8. Testimonials Section (Animated review cards) */}
      <section className="py-24 sm:py-32 bg-white relative overflow-hidden border-t border-zinc-200">
        <div className="container mx-auto px-6 sm:px-12 relative z-10">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-16 items-center">
            <div className="lg:col-span-4">
              <div className="inline-block text-[10px] font-black tracking-widest text-black uppercase mb-4 bg-[#c5f915] px-3 py-1.5">COMMUNITY PROOF</div>
              <h2 className="text-3xl sm:text-5xl font-unbounded font-black uppercase text-zinc-900 leading-tight">
                THE ARENA VERDICTS
              </h2>
              <p className="text-zinc-600 mt-4 font-outfit text-sm leading-relaxed">
                Check why we are rated 4.5 Stars with 18+ google reviews by local fitness practitioners in Nizamabad.
              </p>
              <div className="flex gap-4 mt-8">
                <button 
                  data-testid="testimonial-prev"
                  onClick={() => setActiveTestimonial((prev) => (prev === 0 ? testimonials.length - 1 : prev - 1))}
                  className="border border-zinc-300 hover:border-[#c5f915] hover:bg-[#c5f915] p-3 text-zinc-900 bg-white transition-colors"
                >
                  <ChevronDown className="w-5 h-5 transform rotate-90" />
                </button>
                <button 
                  data-testid="testimonial-next"
                  onClick={() => setActiveTestimonial((prev) => (prev === testimonials.length - 1 ? 0 : prev + 1))}
                  className="border border-zinc-300 hover:border-[#c5f915] hover:bg-[#c5f915] p-3 text-zinc-900 bg-white transition-colors"
                >
                  <ChevronDown className="w-5 h-5 transform -rotate-90" />
                </button>
              </div>
            </div>

            {/* Carousel display with unmounting effects */}
            <div className="lg:col-span-8">
              <div className="bg-zinc-900 p-8 sm:p-12 border border-zinc-900 min-h-[250px] flex flex-col justify-between relative">
                <div>
                  <div className="flex items-center gap-1.5 mb-6 text-[#c5f915]">
                    {[...Array(testimonials[activeTestimonial].rating)].map((_, i) => (
                      <Star key={i} className="w-5 h-5 fill-current" />
                    ))}
                  </div>
                  <blockquote className="text-lg sm:text-2xl font-outfit text-white font-medium italic mb-6 leading-relaxed">
                    "{testimonials[activeTestimonial].review}"
                  </blockquote>
                </div>
                <div className="flex justify-between items-center border-t border-zinc-800 pt-6 mt-6">
                  <div>
                    <cite className="not-italic font-unbounded font-black text-xs text-white uppercase block">
                      {testimonials[activeTestimonial].name}
                    </cite>
                    <span className="text-[10px] text-zinc-500 uppercase tracking-widest block mt-1">Nizamabad Citizen</span>
                  </div>
                  <span className="text-[10px] font-bold text-black bg-[#c5f915] px-3 py-1 uppercase font-unbounded">
                    {testimonials[activeTestimonial].date}
                  </span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* 9. Interactive Booking Wizard (Split Screen) */}
      <section id="booking-section" className="py-24 sm:py-32 bg-white relative border-b border-zinc-200">
        <div className="container mx-auto px-6 sm:px-12">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-16 items-stretch">
            {/* Left Col: Location, Contacts, and Map */}
            <div className="lg:col-span-5 flex flex-col justify-between">
              <div>
                <div className="inline-block text-[10px] font-black tracking-widest text-black uppercase mb-4 bg-[#c5f915] px-3 py-1.5">BOOK YOUR SLOT</div>
                <h2 className="text-3xl sm:text-5xl font-unbounded font-black uppercase text-zinc-900 leading-tight mb-6">
                  APPOINTMENT LOCKDOWN
                </h2>
                <p className="text-zinc-600 font-outfit text-sm leading-relaxed mb-8">
                  Lock your time slot for any service and instantly reserve your place without any profile signup. Show up, experience high-end training, and achieve metrics.
                </p>

                {/* Location Contacts */}
                <div className="space-y-6 font-outfit text-sm">
                  <div className="flex gap-4">
                    <MapPin className="w-5 h-5 text-[#c5f915] shrink-0 mt-1" />
                    <div>
                      <span className="text-[10px] text-zinc-500 uppercase tracking-widest block mb-0.5">ADDRESS LOCATION</span>
                      <p className="text-zinc-900 font-bold leading-normal">
                        Opp. Telangana Grameena Bank, Tilak Gardens, Khaleelwadi, Nizamabad, Telangana 503003, India
                      </p>
                    </div>
                  </div>
                  <div className="flex gap-4">
                    <Phone className="w-5 h-5 text-[#c5f915] shrink-0 mt-1" />
                    <div>
                      <span className="text-[10px] text-zinc-500 uppercase tracking-widest block mb-0.5">HOTLINE MOBILE</span>
                      <a href="tel:+919390919564" className="text-zinc-900 hover:text-[#84a600] font-bold text-base block transition-colors">
                        +91 93909 19564
                      </a>
                    </div>
                  </div>
                  <div className="flex gap-4">
                    <Mail className="w-5 h-5 text-[#c5f915] shrink-0 mt-1" />
                    <div>
                      <span className="text-[10px] text-zinc-500 uppercase tracking-widest block mb-0.5">EMAIL CORRESPONDENCE</span>
                      <a href="mailto:support@wellnessarena.in" className="text-zinc-900 hover:text-[#84a600] font-bold block transition-colors">
                        support@wellnessarena.in
                      </a>
                    </div>
                  </div>
                </div>
              </div>

              {/* Live WhatsApp Action Block */}
              <div className="bg-zinc-50 p-6 border border-zinc-200 mt-10">
                <span className="text-[9px] tracking-widest text-zinc-500 font-bold block mb-1 uppercase">// INSTANT SECURE DISPATCH</span>
                <h4 className="text-xs font-unbounded font-black text-zinc-900 uppercase mb-3">RUSH BOOKING OVER WHATSAPP?</h4>
                <a 
                  href="https://wa.me/919390919564" 
                  target="_blank" 
                  rel="noreferrer"
                  className="inline-flex items-center gap-2 bg-[#25D366] hover:bg-[#20ba5a] text-black font-black uppercase text-xs px-5 py-3 tracking-wider transition-colors"
                >
                  DISPATCH TO +91 93909 19564
                </a>
              </div>
            </div>

            {/* Right Col: Sleek Interactive Form */}
            <div className="lg:col-span-7 bg-[#111] border border-zinc-800 p-8 sm:p-10 relative">
              {bookingSuccess ? (
                <div 
                  data-testid="booking-success-box"
                  className="h-full flex flex-col justify-center items-center text-center py-10"
                >
                  <div className="w-16 h-16 bg-[#c5f915]/10 text-[#c5f915] border-2 border-[#c5f915] flex items-center justify-center mb-6">
                    <Check className="w-8 h-8" />
                  </div>
                  <h3 className="text-2xl font-unbounded font-black text-white uppercase mb-2">SLOT RESERVATION CONFIRMED</h3>
                  <p className="text-zinc-400 font-outfit text-sm max-w-sm mb-6 leading-relaxed">
                    We have registered your details in the Arena control unit database. Show up for your slot at our Nizamabad branch opposite Telangana Grameena Bank!
                  </p>
                  <button 
                    onClick={() => setBookingSuccess(false)}
                    className="border border-[#c5f915] text-[#c5f915] font-black uppercase tracking-wider px-6 py-3 text-xs hover:bg-[#c5f915]/10 transition-colors rounded-none"
                  >
                    BOOK ANOTHER SLOT
                  </button>
                </div>
              ) : (
                <form onSubmit={handleBookingSubmit} className="space-y-5">
                  <div className="border-b border-zinc-800 pb-4 mb-6">
                    <span className="text-[10px] tracking-widest text-zinc-500 font-black uppercase block mb-1">SECURE ACCESS</span>
                    <h3 className="text-lg sm:text-xl font-unbounded font-black text-white uppercase leading-none">RESERVATION FORM</h3>
                  </div>

                  {bookingError && (
                    <div className="bg-[#ff3b30]/10 border border-[#ff3b30] p-4 text-xs font-bold text-[#ff3b30] uppercase">
                      Error: {bookingError}
                    </div>
                  )}

                  {/* Form Grid */}
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                    {/* Name */}
                    <div className="flex flex-col">
                      <label className="text-[10px] font-bold uppercase text-zinc-400 mb-1.5 tracking-wider">Full Name *</label>
                      <input 
                        type="text" 
                        required
                        data-testid="booking-name-input"
                        placeholder="e.g. Arjun Kumar"
                        className="bg-black border border-zinc-800 p-3 text-white text-sm focus:border-[#c5f915] focus:outline-none rounded-none placeholder:text-zinc-700"
                        value={bookingForm.client_name}
                        onChange={(e) => setBookingForm({...bookingForm, client_name: e.target.value})}
                      />
                    </div>

                    {/* Email */}
                    <div className="flex flex-col">
                      <label className="text-[10px] font-bold uppercase text-zinc-400 mb-1.5 tracking-wider">Email Address *</label>
                      <input 
                        type="email" 
                        required
                        data-testid="booking-email-input"
                        placeholder="e.g. arjun@gmail.com"
                        className="bg-black border border-zinc-800 p-3 text-white text-sm focus:border-[#c5f915] focus:outline-none rounded-none placeholder:text-zinc-700"
                        value={bookingForm.email}
                        onChange={(e) => setBookingForm({...bookingForm, email: e.target.value})}
                      />
                    </div>

                    {/* Phone */}
                    <div className="flex flex-col">
                      <label className="text-[10px] font-bold uppercase text-zinc-400 mb-1.5 tracking-wider">Phone number *</label>
                      <input 
                        type="tel" 
                        required
                        data-testid="booking-phone-input"
                        placeholder="e.g. +91 98765 43210"
                        className="bg-black border border-zinc-800 p-3 text-white text-sm focus:border-[#c5f915] focus:outline-none rounded-none placeholder:text-zinc-700"
                        value={bookingForm.phone}
                        onChange={(e) => setBookingForm({...bookingForm, phone: e.target.value})}
                      />
                    </div>

                    {/* Service */}
                    <div className="flex flex-col">
                      <label className="text-[10px] font-bold uppercase text-zinc-400 mb-1.5 tracking-wider">Gym Service *</label>
                      <select 
                        data-testid="booking-service-select"
                        className="bg-black border border-zinc-800 p-3 text-white text-sm focus:border-[#c5f915] focus:outline-none rounded-none"
                        value={bookingForm.service}
                        onChange={(e) => setBookingForm({...bookingForm, service: e.target.value})}
                      >
                        <option value="Crossfit">CrossFit Arena</option>
                        <option value="Aerobics">Aerobics Sync</option>
                        <option value="Aquatics">Aquatics Zone</option>
                        <option value="Child care">Child Care</option>
                        <option value="Cycling">Spin Cycling</option>
                        <option value="Dance fitness classes">Dance Fitness classes</option>
                        <option value="Pilates">Pilates Core</option>
                        <option value="Adult sports">Adult Sports league</option>
                      </select>
                    </div>

                    {/* Booking Date */}
                    <div className="flex flex-col">
                      <label className="text-[10px] font-bold uppercase text-zinc-400 mb-1.5 tracking-wider">Desired Date *</label>
                      <input 
                        type="date" 
                        required
                        data-testid="booking-date-input"
                        className="bg-black border border-zinc-800 p-3 text-white text-sm focus:border-[#c5f915] focus:outline-none rounded-none"
                        value={bookingForm.booking_date}
                        onChange={(e) => setBookingForm({...bookingForm, booking_date: e.target.value})}
                      />
                    </div>

                    {/* Booking Time */}
                    <div className="flex flex-col">
                      <label className="text-[10px] font-bold uppercase text-zinc-400 mb-1.5 tracking-wider">Time Slot *</label>
                      <select 
                        required
                        data-testid="booking-time-select"
                        className="bg-black border border-zinc-800 p-3 text-white text-sm focus:border-[#c5f915] focus:outline-none rounded-none"
                        value={bookingForm.time_slot}
                        onChange={(e) => setBookingForm({...bookingForm, time_slot: e.target.value})}
                      >
                        <option value="">Select Time Slot</option>
                        <option value="05:30 AM - 07:00 AM">Morning Early: 05:30 AM - 07:00 AM</option>
                        <option value="07:00 AM - 09:00 AM">Morning Mid: 07:00 AM - 09:00 AM</option>
                        <option value="09:00 AM - 12:00 PM">Morning Late: 09:00 AM - 12:00 PM</option>
                        <option value="05:00 PM - 06:30 PM">Evening Early: 05:00 PM - 06:30 PM</option>
                        <option value="06:30 PM - 08:00 PM">Evening Mid: 06:30 PM - 08:00 PM</option>
                        <option value="08:00 PM - 10:00 PM">Evening Late: 08:00 PM - 10:00 PM</option>
                      </select>
                    </div>

                    {/* Package comparison filled */}
                    <div className="sm:col-span-2 grid grid-cols-2 gap-4 bg-black border border-zinc-850 p-4">
                      <div>
                        <span className="text-[9px] text-zinc-500 uppercase block">Selected Package</span>
                        <span className="text-[#c5f915] font-unbounded text-xs font-black uppercase">{bookingForm.package}</span>
                      </div>
                      <div className="text-right">
                        <span className="text-[9px] text-zinc-500 uppercase block">Simulated checkout</span>
                        <span className="text-white font-unbounded text-xs font-black">₹{bookingForm.price.toLocaleString("en-IN")}</span>
                      </div>
                    </div>

                    {/* Notes */}
                    <div className="sm:col-span-2 flex flex-col">
                      <label className="text-[10px] font-bold uppercase text-zinc-400 mb-1.5 tracking-wider">Fitness Goals or Notes (Optional)</label>
                      <textarea 
                        rows="2"
                        data-testid="booking-notes-input"
                        placeholder="e.g. Muscle building focus, knee concerns..."
                        className="bg-black border border-zinc-800 p-3 text-white text-sm focus:border-[#c5f915] focus:outline-none rounded-none placeholder:text-zinc-700 resize-none"
                        value={bookingForm.notes}
                        onChange={(e) => setBookingForm({...bookingForm, notes: e.target.value})}
                      />
                    </div>
                  </div>

                  <button 
                    type="submit"
                    data-testid="booking-submit-button"
                    disabled={bookingLoading}
                    className="w-full bg-[#c5f915] hover:bg-[#d4fc46] text-black font-black uppercase text-xs py-4 tracking-wider transition-colors disabled:opacity-50 rounded-none shadow-[0_0_15px_rgba(197,249,21,0.2)]"
                  >
                    {bookingLoading ? "TRANSMITTING DATA..." : "CONFIRM SLOT BOOKING"}
                  </button>
                </form>
              )}
            </div>
          </div>
        </div>
      </section>

      {/* 10. FAQ Accordion Section */}
      <section className="py-24 sm:py-32 bg-white border-b border-zinc-200">
        <div className="container mx-auto px-6 sm:px-12">
          <div className="text-center max-w-xl mx-auto mb-16">
            <div className="inline-block text-[10px] font-black tracking-widest text-black uppercase mb-4 bg-[#c5f915] px-3 py-1.5">CRITICAL FAQS</div>
            <h2 className="text-3xl sm:text-5xl font-unbounded font-black uppercase text-zinc-900 leading-tight">
              KNOWLEDGE DECK
            </h2>
          </div>

          <div className="max-w-3xl mx-auto space-y-4">
            {faqs.map((faq, index) => {
              const isActive = activeFaq === index;
              return (
                <div 
                  key={index}
                  className="bg-zinc-50 border border-zinc-200 hover:border-[#c5f915] transition-colors"
                >
                  <button 
                    onClick={() => setActiveFaq(isActive ? null : index)}
                    className="w-full text-left p-6 flex justify-between items-center font-unbounded text-xs sm:text-sm text-zinc-900 font-black uppercase tracking-wider"
                  >
                    <span>{faq.q}</span>
                    <ChevronDown className={`w-4 h-4 text-zinc-900 transition-transform ${isActive ? "transform rotate-180" : ""}`} />
                  </button>
                  {isActive && (
                    <div className="px-6 pb-6 text-sm font-outfit text-zinc-600 border-t border-zinc-200 pt-4 leading-relaxed">
                      {faq.a}
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* 11. Contact & Lead Capture Form Section */}
      <section id="contact-section" className="py-24 sm:py-32 bg-zinc-50 relative border-t border-zinc-200">
        <div className="container mx-auto px-6 sm:px-12">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-center">
            {/* Left Col: Capturing */}
            <div>
              <div className="inline-block text-[10px] font-black tracking-widest text-black uppercase mb-4 bg-[#c5f915] px-3 py-1.5">INQUIRY CHANNELS</div>
              <h2 className="text-3xl sm:text-5xl font-unbounded font-black uppercase text-zinc-900 leading-none mb-6">
                DISPATCH INTEL
              </h2>
              <p className="text-zinc-600 font-outfit text-sm leading-relaxed mb-8">
                Got pre-sales queries, group pricing requests, or questions regarding our timing streams? Drop a direct message and let our dispatch unit correspond.
              </p>

              {/* Form fields */}
              {leadSuccess ? (
                <div 
                  data-testid="lead-success-box"
                  className="bg-[#c5f915] border border-[#c5f915] p-6 text-black"
                >
                  <h4 className="font-unbounded font-black text-xs uppercase mb-1">INTEL DISPATCH SUCCESSFUL</h4>
                  <p className="font-outfit text-xs text-black/80">We have received your message. Our Nizamabad center directors will contact you shortly.</p>
                </div>
              ) : (
                <form onSubmit={handleLeadSubmit} className="space-y-4">
                  {leadError && (
                    <div className="bg-red-50 border border-red-400 p-4 text-xs font-bold text-red-600">
                      {leadError}
                    </div>
                  )}
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                    <input 
                      type="text"
                      required
                      data-testid="lead-name-input"
                      placeholder="Your Name *"
                      className="bg-white border border-zinc-300 p-3 text-zinc-900 text-sm focus:border-[#c5f915] focus:outline-none rounded-none placeholder:text-zinc-400"
                      value={leadForm.name}
                      onChange={(e) => setLeadForm({...leadForm, name: e.target.value})}
                    />
                    <input 
                      type="email"
                      required
                      data-testid="lead-email-input"
                      placeholder="Your Email *"
                      className="bg-white border border-zinc-300 p-3 text-zinc-900 text-sm focus:border-[#c5f915] focus:outline-none rounded-none placeholder:text-zinc-400"
                      value={leadForm.email}
                      onChange={(e) => setLeadForm({...leadForm, email: e.target.value})}
                    />
                  </div>
                  <input 
                    type="tel"
                    data-testid="lead-phone-input"
                    placeholder="Mobile Number (Optional)"
                    className="bg-white border border-zinc-300 p-3 w-full text-zinc-900 text-sm focus:border-[#c5f915] focus:outline-none rounded-none placeholder:text-zinc-400"
                    value={leadForm.phone}
                    onChange={(e) => setLeadForm({...leadForm, phone: e.target.value})}
                  />
                  
                  {/* Select Interests Checklist */}
                  <div>
                    <span className="text-[10px] font-bold text-zinc-500 uppercase tracking-widest block mb-2">Interests (Select multiple):</span>
                    <div className="flex flex-wrap gap-2">
                      {["Crossfit", "Aerobics", "Aquatics", "Child care", "Cycling", "Pilates", "Dance fitness classes"].map((interest) => {
                        const active = leadForm.interests.includes(interest);
                        return (
                          <button 
                            type="button"
                            key={interest}
                            onClick={() => handleInterestToggle(interest)}
                            className={`px-3 py-1.5 text-[10px] font-bold uppercase border transition-colors ${
                              active 
                                ? "bg-[#c5f915] text-black border-[#c5f915]" 
                                : "bg-white border-zinc-300 text-zinc-500 hover:border-zinc-900 hover:text-black"
                            }`}
                          >
                            {interest}
                          </button>
                        );
                      })}
                    </div>
                  </div>

                  <textarea 
                    rows="3"
                    data-testid="lead-message-input"
                    placeholder="Enter details about your fitness background or queries..."
                    className="bg-white border border-zinc-300 p-3 w-full text-zinc-900 text-sm focus:border-[#c5f915] focus:outline-none rounded-none placeholder:text-zinc-400 resize-none"
                    value={leadForm.message}
                    onChange={(e) => setLeadForm({...leadForm, message: e.target.value})}
                  />
                  <button 
                    type="submit"
                    data-testid="lead-submit-button"
                    disabled={leadLoading}
                    className="bg-[#c5f915] hover:bg-[#d4fc46] text-black font-black uppercase text-xs px-6 py-4 tracking-wider transition-colors disabled:opacity-50 rounded-none"
                  >
                    {leadLoading ? "TRANSMITTING DATA..." : "DISPATCH MESSAGES"}
                  </button>
                </form>
              )}
            </div>

            {/* Right Col: Map & Operating Hours */}
            <div className="space-y-8 lg:pl-10">
              <div className="bg-white border border-zinc-200 shadow-sm p-8">
                <h3 className="text-sm font-unbounded font-black text-zinc-900 uppercase mb-4">// TIME AND STREAM METRICS</h3>
                <div className="space-y-4 text-xs font-outfit">
                  <div className="flex justify-between border-b border-zinc-200 pb-2">
                    <span className="text-zinc-500 uppercase tracking-widest">MORNING (EVERYDAY)</span>
                    <span className="text-zinc-900 font-bold font-unbounded">05:30 AM - 12:00 PM</span>
                  </div>
                  <div className="flex justify-between border-b border-zinc-200 pb-2">
                    <span className="text-zinc-500 uppercase tracking-widest">EVENING (EVERYDAY)</span>
                    <span className="text-zinc-900 font-bold font-unbounded">05:00 PM - 10:00 PM</span>
                  </div>
                  <div className="flex justify-between border-b border-zinc-200 pb-2">
                    <span className="text-zinc-500 uppercase tracking-widest">LADIES SPLIT HOURS</span>
                    <span className="text-[#84a600] font-black uppercase">UNISEX ALL SLOTS (FULLY SECURED)</span>
                  </div>
                  <div className="flex justify-between pb-2">
                    <span className="text-zinc-500 uppercase tracking-widest">WEEKLY SCHEDULE</span>
                    <span className="text-[#84a600] font-black uppercase">OPEN ALL 7 DAYS</span>
                  </div>
                </div>
              </div>

              {/* Map Embedding Frame */}
              <div className="border border-zinc-200 h-[250px] relative bg-white shadow-sm flex flex-col justify-center items-center text-center p-6">
                <MapPin className="w-8 h-8 text-[#84a600] animate-bounce mb-3" />
                <h4 className="text-xs font-unbounded font-black uppercase text-zinc-900 mb-2">TILAK GARDENS, KHALEELWADI</h4>
                <p className="text-[11px] text-zinc-500 max-w-xs mb-4">
                  Opposite Telangana Grameena Bank, Tilak Gardens, Khaleelwadi, Nizamabad
                </p>
                <a 
                  href="https://maps.google.com/?q=Wellness+Arena+Unisex+Gym+Tilak+Gardens+Khaleelwadi+Nizamabad" 
                  target="_blank" 
                  rel="noreferrer"
                  className="border-2 border-zinc-900 text-zinc-900 font-black text-[10px] tracking-wider uppercase px-4 py-2.5 hover:bg-[#c5f915] hover:border-[#c5f915] transition-all rounded-none"
                >
                  GET GOOGLE DIRECTIONS
                </a>
              </div>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}

// 12. Complete Secret Admin Control Panel (Control Room Grid - MODE B)
function AdminDashboard() {
  const [bookings, setBookings] = useState([]);
  const [leads, setLeads] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [filterTerm, setFilterTerm] = useState("");
  const navigate = useNavigate();

  const fetchAdminData = async () => {
    setLoading(true);
    setError("");
    try {
      const [bookingsRes, leadsRes] = await Promise.all([
        axios.get(`${API}/bookings`),
        axios.get(`${API}/leads`)
      ]);
      setBookings(bookingsRes.data);
      setLeads(leadsRes.data);
    } catch (err) {
      console.error(err);
      setError("Failed to compile databases from API endpoints.");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    document.title = "Admin Dashboard – Wellness Arena";
    const meta = document.createElement("meta");
    meta.name = "robots";
    meta.content = "noindex, nofollow";
    document.head.appendChild(meta);
    fetchAdminData();
    return () => {
      document.title = "Wellness Arena – Premier Unisex A/C Gym in Nizamabad";
      document.head.removeChild(meta);
    };
  }, []);

  const handleUpdateStatus = async (id, currentStatus) => {
    const nextStatus = currentStatus === "Confirmed" ? "Completed" : currentStatus === "Completed" ? "Cancelled" : "Confirmed";
    try {
      const response = await axios.put(`${API}/bookings/${id}`, { status: nextStatus });
      if (response.status === 200) {
        setBookings(prev => prev.map(b => b.id === id ? { ...b, status: nextStatus } : b));
        triggerToastGlobal(`Status updated to ${nextStatus}`, "success");
      }
    } catch (err) {
      console.error(err);
      triggerToastGlobal("Failed to update status", "error");
    }
  };

  const handleDeleteBooking = async (id) => {
    if (!window.confirm("Confirm deletion of this record?")) return;
    try {
      const response = await axios.delete(`${API}/bookings/${id}`);
      if (response.status === 200) {
        setBookings(prev => prev.filter(b => b.id !== id));
        triggerToastGlobal("Booking record deleted", "success");
      }
    } catch (err) {
      console.error(err);
      triggerToastGlobal("Deletion failed", "error");
    }
  };

  const filteredBookings = bookings.filter(b => 
    b.client_name.toLowerCase().includes(filterTerm.toLowerCase()) ||
    b.phone.includes(filterTerm) ||
    b.service.toLowerCase().includes(filterTerm.toLowerCase())
  );

  return (
    <div className="bg-[#050505] min-h-screen py-16 sm:py-24 font-outfit text-white cyber-scanlines">
      <div className="container mx-auto px-6 sm:px-12">
        {/* Top Header */}
        <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4 border-b border-zinc-800 pb-6 mb-10">
          <div>
            <div className="flex items-center gap-2 text-[#c5f915] mb-2">
              <Settings className="w-5 h-5 animate-spin" />
              <span className="text-xs font-black tracking-widest font-unbounded uppercase">CENTRAL DECK</span>
            </div>
            <h1 className="text-2xl sm:text-4xl font-unbounded font-black uppercase text-white leading-none">
              ARENA SYSTEM MANAGER
            </h1>
            <p className="text-zinc-500 text-xs mt-2">Real-time status monitoring for Nizamabad's active bookings.</p>
          </div>
          <div className="flex gap-2">
            <button 
              onClick={fetchAdminData}
              className="bg-[#c5f915]/10 border border-[#c5f915] hover:bg-[#c5f915] hover:text-black font-black uppercase text-xs px-4 py-3 tracking-wider transition-colors rounded-none"
            >
              SYNC SYSTEMS
            </button>
            <button 
              onClick={() => navigate("/")}
              className="border border-zinc-800 hover:border-white text-zinc-400 hover:text-white font-black uppercase text-xs px-4 py-3 tracking-wider transition-colors rounded-none"
            >
              EXIT MANAGER
            </button>
          </div>
        </div>

        {error && (
          <div className="bg-red-500/10 border border-red-500 text-red-500 p-4 font-bold text-xs uppercase mb-8">
            {error}
          </div>
        )}

        {/* Dashboard Grid split into Bookings & Leads */}
        <div className="space-y-12">
          
          {/* Section A: Bookings */}
          <div className="bg-[#111] border border-zinc-800 p-6 sm:p-8">
            <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4 mb-6">
              <h2 className="text-sm font-unbounded font-black text-white uppercase tracking-widest">// SECURE BOOKING MATRIX</h2>
              
              <input 
                type="text"
                data-testid="admin-search-input"
                placeholder="Search Client Name / Phone / Service..."
                className="bg-black border border-zinc-800 px-4 py-2.5 text-xs text-white focus:border-[#c5f915] focus:outline-none rounded-none w-full sm:w-72"
                value={filterTerm}
                onChange={(e) => setFilterTerm(e.target.value)}
              />
            </div>

            {loading ? (
              <div className="py-12 text-center text-xs font-unbounded text-zinc-600 tracking-widest uppercase animate-pulse">
                RETRIEVING MEMORY SEGMENTS...
              </div>
            ) : filteredBookings.length === 0 ? (
              <div className="py-12 text-center text-xs font-outfit text-zinc-500 uppercase">
                Zero matching booking logs registered.
              </div>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full text-left border-collapse text-xs">
                  <thead>
                    <tr className="border-b border-zinc-850 text-zinc-500 uppercase font-black tracking-widest font-unbounded text-[9px]">
                      <th className="py-4 px-3">Client details</th>
                      <th className="py-4 px-3">Chosen service</th>
                      <th className="py-4 px-3">Desired slot date</th>
                      <th className="py-4 px-3">Package tier</th>
                      <th className="py-4 px-3">Database status</th>
                      <th className="py-4 px-3 text-right">Operational Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {filteredBookings.map((b) => (
                      <tr key={b.id} className="border-b border-zinc-900 hover:bg-black/40 transition-colors">
                        <td className="py-4 px-3">
                          <p className="font-bold text-white font-unbounded text-[10px]">{b.client_name}</p>
                          <p className="text-zinc-500 mt-0.5">{b.phone} | {b.email}</p>
                        </td>
                        <td className="py-4 px-3 uppercase font-semibold text-zinc-300">{b.service}</td>
                        <td className="py-4 px-3">
                          <p className="text-white font-bold">{b.booking_date}</p>
                          <p className="text-zinc-500 text-[10px] mt-0.5">{b.time_slot}</p>
                        </td>
                        <td className="py-4 px-3 text-white">
                          <p className="font-bold uppercase text-[#c5f915]">{b.package}</p>
                          <p className="text-zinc-500 mt-0.5">₹{b.price.toLocaleString("en-IN")}</p>
                        </td>
                        <td className="py-4 px-3">
                          <span className={`px-2.5 py-0.5 font-bold tracking-widest uppercase text-[9px] border ${
                            b.status === "Confirmed" ? "bg-emerald-500/10 text-emerald-400 border-emerald-500/30" :
                            b.status === "Completed" ? "bg-[#c5f915]/10 text-[#c5f915] border-[#c5f915]/30" :
                            "bg-red-500/10 text-red-400 border-red-500/30"
                          }`}>
                            {b.status}
                          </span>
                        </td>
                        <td className="py-4 px-3 text-right space-x-2">
                          <button 
                            data-testid="admin-update-status-button"
                            onClick={() => handleUpdateStatus(b.id, b.status)}
                            className="bg-zinc-900 border border-zinc-800 hover:border-[#c5f915] text-[#c5f915] px-2.5 py-1.5 font-black uppercase text-[9px] tracking-wider transition-colors"
                          >
                            CYCLE STATUS
                          </button>
                          <button 
                            data-testid="admin-delete-booking-button"
                            onClick={() => handleDeleteBooking(b.id)}
                            className="bg-red-500/10 border border-red-500/30 hover:bg-red-500 hover:text-white text-red-400 p-1.5 transition-colors"
                          >
                            <Trash2 className="w-3.5 h-3.5" />
                          </button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>

          {/* Section B: Leads Inquiries */}
          <div className="bg-[#111] border border-zinc-800 p-6 sm:p-8">
            <h2 className="text-sm font-unbounded font-black text-white uppercase tracking-widest mb-6">// Captured Leads & Inquiries</h2>
            
            {loading ? (
              <div className="py-12 text-center text-xs font-unbounded text-zinc-600 tracking-widest uppercase animate-pulse">
                RETRIEVING LEADS MEMORY...
              </div>
            ) : leads.length === 0 ? (
              <div className="py-12 text-center text-xs font-outfit text-zinc-500 uppercase">
                Zero marketing lead records registered.
              </div>
            ) : (
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                {leads.map((l) => (
                  <div key={l.id} className="bg-black p-5 border border-zinc-850 flex flex-col justify-between">
                    <div>
                      <div className="flex justify-between items-start mb-3 border-b border-zinc-900 pb-2">
                        <div>
                          <h3 className="font-unbounded font-black text-[11px] text-white uppercase">{l.name}</h3>
                          <p className="text-zinc-500 text-[10px] mt-0.5">{l.email} {l.phone && `| ${l.phone}`}</p>
                        </div>
                        <span className="text-[9px] text-[#c5f915] font-black tracking-widest uppercase font-unbounded">
                          LEAD CHANNELS
                        </span>
                      </div>
                      <p className="text-zinc-400 font-outfit text-xs leading-relaxed mb-4">
                        "{l.message || 'No direct inquiry message attached.'}"
                      </p>
                    </div>

                    {l.interests && l.interests.length > 0 && (
                      <div className="flex flex-wrap gap-1.5 border-t border-zinc-900 pt-3">
                        {l.interests.map((interest) => (
                          <span key={interest} className="bg-[#c5f915]/10 text-[#c5f915] border border-[#c5f915]/20 text-[9px] font-bold px-2 py-0.5 uppercase tracking-wide">
                            {interest}
                          </span>
                        ))}
                      </div>
                    )}
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

// Global Main Layout Shell
function MainLayout({ children }) {
  const navigate = useNavigate();

  return (
    <div className="bg-white min-h-screen text-zinc-900 flex flex-col justify-between">
      {/* Dynamic Header Navbar */}
      <header className="sticky top-0 z-40 bg-white/90 backdrop-blur-md border-b border-zinc-200 font-unbounded">
        <div className="container mx-auto px-6 sm:px-12 py-5 flex justify-between items-center">
          {/* Logo */}
          <div className="flex items-center gap-2 cursor-pointer select-none" onClick={() => navigate("/")}>
            <div className="bg-black p-1.5">
              <Dumbbell className="w-4 h-4 text-[#c5f915]" />
            </div>
            <span className="text-xs sm:text-sm font-black tracking-tighter text-zinc-900">WELLNESS ARENA</span>
          </div>

          {/* Navigation Links */}
          <nav className="hidden lg:flex items-center gap-8 text-[11px] font-black tracking-widest text-zinc-500">
            <a href="/#home" data-testid="nav-link-home" className="hover:text-black uppercase transition-colors">HOME</a>
            <a href="/#about" className="hover:text-black uppercase transition-colors">ABOUT</a>
            <a href="/#services-section" data-testid="nav-link-services" className="hover:text-black uppercase transition-colors">SERVICES</a>
            <a href="/#pricing-section" data-testid="nav-link-pricing" className="hover:text-black uppercase transition-colors">PACKAGES</a>
            <a href="/#contact-section" className="hover:text-black uppercase transition-colors">CONTACT</a>
            <Link to="/admin" data-testid="nav-link-admin" className="hover:text-black text-zinc-400 uppercase transition-colors">// SYSTEM MANAGER</Link>
          </nav>

          {/* Magnetic call to action */}
          <div className="flex items-center gap-4">
            <a 
              href="/#booking-section"
              data-testid="nav-book-button"
              className="bg-[#c5f915] hover:bg-[#d4fc46] text-black font-black uppercase text-[10px] tracking-wider px-5 py-3 transition-transform hover:scale-105 rounded-none shadow-[0_0_15px_rgba(197,249,21,0.25)]"
            >
              APPOINTMENT BOOKING
            </a>
          </div>
        </div>
      </header>

      {/* Primary views */}
      <main className="flex-grow">
        {children}
      </main>

      {/* 13. Dynamic Cinematic Footer */}
      <footer className="bg-black border-t border-zinc-900 py-16 font-outfit text-sm text-zinc-500">
        <div className="container mx-auto px-6 sm:px-12">
          <div className="grid grid-cols-1 md:grid-cols-12 gap-12 mb-12">
            <div className="md:col-span-4 space-y-4">
              <div className="flex items-center gap-2">
                <Dumbbell className="w-6 h-6 text-[#c5f915]" />
                <span className="text-white font-unbounded font-black text-sm tracking-tighter">WELLNESS ARENA</span>
              </div>
              <p className="text-xs text-zinc-400 max-w-sm leading-relaxed">
                Nizamabad's high-octane luxury unisex arena. Redefining human energy levels through heavy-lifting rigs, high-intensity aerobics, and secure childcare facilities.
              </p>
            </div>

            <div className="md:col-span-3 space-y-3 text-xs">
              <span className="font-unbounded font-black uppercase text-[10px] tracking-widest text-[#c5f915] block mb-2">// QUICK NAVIGATION</span>
              <a href="/#home" className="block hover:text-white uppercase transition-colors">Return to Home</a>
              <a href="/#about" className="block hover:text-white uppercase transition-colors">Our Manifesto</a>
              <a href="/#services-section" className="block hover:text-white uppercase transition-colors">Core Training segments</a>
              <a href="/#pricing-section" className="block hover:text-white uppercase transition-colors">Membership Matrix</a>
            </div>

            <div className="md:col-span-5 space-y-4">
              <span className="font-unbounded font-black uppercase text-[10px] tracking-widest text-[#c5f915] block mb-2">// DISPATCH FEEDBACK</span>
              <p className="text-xs text-zinc-400 leading-relaxed">
                Subscribe to receive elite workout blueprints, nutrition hacks, and event bookings alerts.
              </p>
              <div className="flex bg-[#111] border border-zinc-850 p-1">
                <input 
                  type="email" 
                  placeholder="ENTER SECURED EMAIL" 
                  className="bg-transparent border-0 px-3 py-2 text-xs text-white focus:outline-none flex-grow"
                />
                <button 
                  onClick={() => triggerToastGlobal("Secured dispatch channel subscribed!", "success")}
                  className="bg-[#c5f915] hover:bg-[#d4fc46] text-black font-black uppercase text-[10px] tracking-wider px-4 transition-colors"
                >
                  SUBSCRIBE
                </button>
              </div>
            </div>
          </div>

          <div className="border-t border-zinc-900 pt-8 flex flex-col sm:flex-row justify-between items-center gap-4 text-xs">
            <div>
              <p>Opp. Telangana Grameena Bank, Tilak Gardens, Khaleelwadi, Nizamabad - 503003</p>
              <p className="mt-1 text-zinc-600">Tel: +91 93909 19564 | Email: support@wellnessarena.in</p>
            </div>
            <div className="text-center sm:text-right text-zinc-600">
              <p>© 2025 Wellness Arena Gym. All Core Secured.</p>
              <p className="mt-1 uppercase text-[9px] tracking-widest">Designed for peak human performance</p>
            </div>
          </div>
        </div>
      </footer>
    </div>
  );
}

function MainApp() {
  const [loading, setLoading] = useState(!(typeof window !== "undefined" && window.location.search.includes("noload")));

  return (
    <>
      <ToastNotification />
      <AnimatePresence>
        {loading && <LoadingScreen onComplete={() => setLoading(false)} />}
      </AnimatePresence>

      {!loading && (
        <Routes>
          <Route path="/" element={<MainLayout><LandingPage /></MainLayout>} />
          <Route path="/admin" element={<AdminDashboard />} />
        </Routes>
      )}
    </>
  );
}

function App() {
  return (
    <div className="App bg-[#050505]">
      <BrowserRouter>
        <MainApp />
      </BrowserRouter>
    </div>
  );
}

export default App;
